import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import {
  Route,
  CheckCircle2,
  XCircle,
  ExternalLink,
  Plus,
  Flag,
  Timer,
  RefreshCw,
  Spline,
  Share2,
  HelpCircle,
  EyeOff,
  Calendar,
  X,
} from 'lucide-react'
import { toast } from 'sonner'
import { supabase } from '@/integrations/supabase/client'
import { useRoadStore, occurrenceKey, occurrenceOf } from '@/lib/roads/store'
import {
  formatDuration,
  invalidateRoadGeometry,
  isRoughEstimate,
  roadDurationSeconds,
  roadElevation,
  trimKeptFraction,
  trimScaledDurationSeconds,
  useDurationsVersion,
  useElevationVersion,
} from '@/lib/roads/api'
import { useAuth } from '@/lib/auth/useAuth'
import { roadShareUrl, copyText } from '@/lib/roads/share'
import BikeTypeTip from '@/components/ui/BikeTypeTip'

const BIKE_COLORS: Record<string, string> = {
  Sport: '#FF4438',
  ADV: '#34D26B',
  Sumo: '#FF9F1C',
  Cruiser: '#3B82F6',
}

const RATING_LABELS: { key: keyof NonNullable<ReturnType<typeof getRatings>>; label: string }[] = []
function getRatings(r: unknown) {
  return (r as { ratings: Record<string, number | null> }).ratings
}

// Compact elevation profile: amber area chart spanning the full panel width,
// one x-position per polyline point (no downsampling). Null samples carry the
// previous value forward so gaps don't notch the line.
function ElevationChart({ elevation }: { elevation: (number | null)[] }) {
  const numeric = elevation.filter((v): v is number => typeof v === 'number')
  if (numeric.length < 2) {
    return (
      <div>
        <div className="flex items-baseline justify-between mb-1">
          <span className="text-[10px] font-mono uppercase tracking-widest text-white/40">
            Elevation
          </span>
        </div>
        <div className="w-full h-16 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center">
          <span className="text-xs font-mono text-white/40">no data</span>
        </div>
      </div>
    )
  }
  const min = Math.min(...numeric)
  const max = Math.max(...numeric)
  const span = Math.max(1, max - min)
  const n = elevation.length
  let prev = numeric[0]
  const pts = elevation.map((v, i) => {
    if (typeof v === 'number') prev = v
    const x = n > 1 ? (i / (n - 1)) * 100 : 0
    const y = 38 - ((typeof v === 'number' ? v : prev) - min) / span * 34
    return `${x.toFixed(2)},${Math.max(2, Math.min(38, y)).toFixed(2)}`
  })
  const line = pts.join(' ')
  const ft = (m: number) => Math.round(m * 3.28084).toLocaleString('en-US')
  let gainM = 0
  for (let i = 1; i < elevation.length; i++) {
    const a = elevation[i - 1]
    const b = elevation[i]
    if (typeof a === 'number' && typeof b === 'number' && b > a) gainM += b - a
  }
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-[10px] font-mono uppercase tracking-widest text-white/40">
          Elevation
        </span>
        <span className="text-[11px] font-mono text-white/50">
          {ft(min)}–{ft(max)} ft · {ft(gainM)} ft climbing
        </span>
      </div>
      <div className="relative">
        <svg
          viewBox="0 0 100 40"
          preserveAspectRatio="none"
          className="w-full h-16 block"
          aria-label="Road elevation profile"
        >
          <defs>
            <linearGradient id="elevFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#FFB800" stopOpacity="0.32" />
              <stop offset="100%" stopColor="#FFB800" stopOpacity="0.02" />
            </linearGradient>
          </defs>
          {[0.25, 0.5, 0.75].map((f) => (
            <line
              key={f}
              x1="0"
              x2="100"
              y1={40 * f}
              y2={40 * f}
              stroke="#FFFFFF"
              strokeOpacity="0.12"
              strokeWidth="0.5"
              vectorEffect="non-scaling-stroke"
            />
          ))}
          <polygon points={`0,40 ${line} 100,40`} fill="url(#elevFill)" />
          <polyline
            points={line}
            fill="none"
            stroke="#FFB800"
            strokeWidth="1.5"
            vectorEffect="non-scaling-stroke"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        </svg>
        <span className="absolute left-1 top-1 text-[9px] font-mono text-white/40 pointer-events-none">
          {ft(max)} ft
        </span>
        <span className="absolute -left-0.5 bottom-0 text-[9px] font-mono text-white/40 pointer-events-none [writing-mode:vertical-rl] rotate-180 select-none">
          ft
        </span>
        <span className="absolute right-0 -bottom-3.5 text-[9px] font-mono text-white/40 pointer-events-none select-none">
          mi
        </span>
      </div>
    </div>
  )
}

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    // Sandboxed iframes deny the async Clipboard API — fall back to the
    // legacy execCommand path, which still works there.
    try {
      const ta = document.createElement('textarea')
      ta.value = text
      ta.setAttribute('readonly', '')
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      const ok = document.execCommand('copy')
      document.body.removeChild(ta)
      return ok
    } catch {
      return false
    }
  }
}

export default function RoadPanel() {
  const { session } = useAuth()
  const roads = useRoadStore((s) => s.roads)
  const selectedId = useRoadStore((s) => s.selectedId)
  const selectRoad = useRoadStore((s) => s.selectRoad)
  const routeIds = useRoadStore((s) => s.routeIds)
  const routeTrims = useRoadStore((s) => s.routeTrims)
  const selectedVisitKey = useRoadStore((s) => s.selectedVisitKey)
  const addToRoute = useRoadStore((s) => s.addToRoute)
  const setTrimmingRoad = useRoadStore((s) => s.setTrimmingRoad)
  const replaceRoad = useRoadStore((s) => s.replaceRoad)
  const setRecalculating = useRoadStore((s) => s.setRecalculating)
  const setRecalcWatchId = useRoadStore((s) => s.setRecalcWatchId)
  const recalcWatchId = useRoadStore((s) => s.recalcWatchId)
  const recalcDrawnId = useRoadStore((s) => s.recalcDrawnId)
  const setRecalcDrawn = useRoadStore((s) => s.setRecalcDrawn)
  const trimmingRoadId = useRoadStore((s) => s.trimmingRoadId)

  const [showTimeTip, setShowTimeTip] = useState(false)
  const [shareCopied, setShareCopied] = useState(false)
  const shareCopiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [sheetH, setSheetH] = useState<number | null>(null)
  const asideRef = useRef<HTMLElement | null>(null)
  const dragRef = useRef<{ startY: number; startH: number } | null>(null)
  const dragRafRef = useRef<number | null>(null)
  const pendingHRef = useRef<number | null>(null)
  // During the drag the height is written straight to the DOM — zero React
  // re-renders (the map keeps its headroom); state catches up once at the end.
  const applyDragHeight = (h: number) => {
    const el = asideRef.current
    if (el) {
      el.style.height = `${h}px`
      el.style.maxHeight = `${h}px`
    }
  }

  // Pointer-based drag so the header works with touch AND mouse. The whole
  // black header block (grab bar + title area) is the drag-sensitive zone;
  // pointer capture keeps the gesture alive when the finger/cursor leaves it.
  const onDragStart = (e: React.PointerEvent) => {
    // Drag-to-resize is a mobile bottom-sheet affordance only — on desktop
    // the panel is anchored to the top-right and must not be resizable.
    if (window.matchMedia('(min-width: 768px)').matches) return
    if (e.pointerType === 'mouse' && e.button !== 0) return
    const current =
      sheetH ??
      asideRef.current?.offsetHeight ??
      Math.round(window.innerHeight * 0.4)
    dragRef.current = { startY: e.clientY, startH: current }
    try {
      ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    } catch {
      /* no capture available */
    }
  }
  const onDragMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return
    const dy = dragRef.current.startY - e.clientY
    const h = Math.min(
      Math.max(dragRef.current.startH + dy, 140),
      Math.round(window.innerHeight * 0.85),
    )
    pendingHRef.current = h
    if (dragRafRef.current == null) {
      dragRafRef.current = requestAnimationFrame(() => {
        dragRafRef.current = null
        if (pendingHRef.current != null) applyDragHeight(pendingHRef.current)
      })
    }
  }
  const onDragEnd = () => {
    if (dragRafRef.current != null) {
      cancelAnimationFrame(dragRafRef.current)
      dragRafRef.current = null
    }
    const h = pendingHRef.current
    pendingHRef.current = null
    dragRef.current = null
    if (h != null) {
      applyDragHeight(h)
      setSheetH(h)
    }
  }
  // A dragged sheet height follows the user across road selections while it
  // is SHORTER than the default 40% screen height (treat it as their
  // preference). If it is TALLER than the default, reset it: an over-tall
  // sheet covers the road the camera just framed on selection.
  useEffect(() => {
    setSheetH((h) => {
      if (h == null) return h
      const defaultH = Math.round(window.innerHeight * 0.4)
      return h > defaultH ? null : h
    })
  }, [selectedId])
  // On mobile, entering trim from the road panel shrinks the sheet to the
  // route tray's height so the trim bubbles and the map stay in view; leaving
  // trim restores whatever height the user had before.
  const trimSavedHRef = useRef<number | null>(null)
  useEffect(() => {
    if (window.matchMedia('(min-width: 768px)').matches) return
    if (trimmingRoadId) {
      if (trimSavedHRef.current == null) {
        const el = asideRef.current
        const inlineH = el && el.style.height ? parseFloat(el.style.height) : NaN
        trimSavedHRef.current = Number.isFinite(inlineH) ? inlineH : null
      }
      const tray = document.querySelector('[data-route-tray]')
      const trayH = tray ? tray.getBoundingClientRect().height : 0
      const h = Math.min(
        Math.max(140, Math.round(trayH || window.innerHeight * 0.4)),
        Math.round(window.innerHeight * 0.4),
      )
      applyDragHeight(h)
      setSheetH(h)
    } else if (trimSavedHRef.current != null) {
      const saved = trimSavedHRef.current
      trimSavedHRef.current = null
      if (saved != null) {
        applyDragHeight(saved)
        setSheetH(saved)
      } else {
        const el = asideRef.current
        if (el) {
          el.style.height = ''
          el.style.maxHeight = ''
        }
        setSheetH(null)
      }
    }
  }, [trimmingRoadId])
  const [flagOpen, setFlagOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const scrollBodyRef = useRef<HTMLDivElement | null>(null)
  const flagBtnRef = useRef<HTMLButtonElement | null>(null)
  const flagAnchorY = useRef<number | null>(null)
  // Stabilization loop: keeps re-anchoring the flag toggle for a short
  // window after opening, because async content above the button
  // (description, ratings, duration chip) pops in over several frames on a
  // freshly selected road. Cleared on close/road change.
  const flagAnchorRaf = useRef<number | null>(null)

  const stopFlagAnchor = () => {
    if (flagAnchorRaf.current != null) {
      cancelAnimationFrame(flagAnchorRaf.current)
      flagAnchorRaf.current = null
    }
    flagAnchorY.current = null
  }

  const toggleFlag = () => {
    if (flagOpen) {
      setFlagOpen(false)
      stopFlagAnchor()
      return
    }
    const btn = flagBtnRef.current
    const body = scrollBodyRef.current
    flagAnchorY.current =
      btn && body
        ? btn.getBoundingClientRect().top - body.getBoundingClientRect().top
        : null
    setFlagOpen(true)
  }

  // Opening the flag form expands content ABOVE the toggle, pushing the
  // button down the panel. Re-anchor it in the SAME layout pass (before
  // paint — no flicker) by shifting the panel's scroll by the exact delta.
  // The button's Y is measured RELATIVE TO THE SCROLL CONTAINER, not the
  // viewport: the panel itself slide-animates on every road switch
  // (key={road.id} remount), so a viewport-relative anchor would include
  // the panel's own animation offset and corrupt the correction.
  useLayoutEffect(() => {
    if (!flagOpen) return
    const targetY = flagAnchorY.current
    if (targetY == null) return
    const btn = flagBtnRef.current
    const body = scrollBodyRef.current
    if (!btn || !body) return

    // Async data (ratings, duration) can arrive several hundred ms after
    // the open, once the layout already looked settled — so watch for a
    // full window instead of a few frames. Manual scrolling cancels it.
    let stableFrames = 0
    let cancelled = false
    const start = performance.now()
    const cancel = () => {
      cancelled = true
      body.removeEventListener('wheel', cancel)
      body.removeEventListener('touchmove', cancel)
    }
    body.addEventListener('wheel', cancel, { passive: true })
    body.addEventListener('touchmove', cancel, { passive: true })

    const tick = () => {
      if (cancelled) return
      const delta =
        btn.getBoundingClientRect().top - body.getBoundingClientRect().top - targetY
      if (Math.abs(delta) > 0.5) {
        body.scrollTop += delta
        stableFrames = 0
      } else {
        stableFrames++
      }
      if (stableFrames < 8 && performance.now() - start < 2000) {
        flagAnchorRaf.current = requestAnimationFrame(tick)
      } else {
        cancel()
      }
    }
    const delta =
      btn.getBoundingClientRect().top - body.getBoundingClientRect().top - targetY
    if (Math.abs(delta) > 0.5) body.scrollTop += delta
    flagAnchorRaf.current = requestAnimationFrame(tick)
    return () => {
      cancel()
      stopFlagAnchor()
    }
  }, [flagOpen, selectedId])

  // The flag form is per-road context — collapse it whenever the user
  // deselects or switches to another road.
  useEffect(() => {
    setFlagOpen(false)
    setReason('')
    stopFlagAnchor()
  }, [selectedId])
  const { isModerator } = useAuth()
  const [resetting, setResetting] = useState(false)
  const [interpRunning, setInterpRunning] = useState(false)

  // Hide Road (moderator): popup form with reason + review date; the road is
  // only hidden once the confirm button is pressed with both fields filled.
  const [hideOpen, setHideOpen] = useState(false)
  const [hideReason, setHideReason] = useState('')
  const [hideReviewDate, setHideReviewDate] = useState('')
  const [hideSubmitting, setHideSubmitting] = useState(false)
  const hideRoadLocally = useRoadStore((s) => s.hideRoadLocally)
  useEffect(() => {
    setHideOpen(false)
    setHideReason('')
    setHideReviewDate('')
    setHideSubmitting(false)
  }, [selectedId])
  const confirmHideRoad = async () => {
    if (!road || hideSubmitting) return
    if (!hideReason.trim() || !hideReviewDate) return
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const picked = new Date(`${hideReviewDate}T00:00:00`)
    if (!(picked > today)) {
      toast.error('Pick a review date in the future.')
      return
    }
    setHideSubmitting(true)
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const userId = sessionData.session?.user?.id
      if (!userId) {
        toast.error('Sign in first to hide this road.')
        setHideSubmitting(false)
        return
      }
      const { error } = await supabase.from('hidden_roads').insert({
        road_id: road.id,
        road_name: road.name,
        reason: hideReason.trim(),
        review_at: hideReviewDate,
        hidden_by: userId,
      })
      if (error) throw new Error(error.message || 'Hide failed')
      hideRoadLocally(road.id)
      toast.success(`"${road.name}" is now hidden from the map and search.`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not hide this road. Try again.')
      setHideSubmitting(false)
    }
  }

  // Durations resolve asynchronously with the road geometry — subscribe to the
  // version counter so the timer chip appears the moment OSRM reports a time.
  useDurationsVersion()
  const road = roads.find((r) => r.id === selectedId) ?? null
  // Horizontal centering of the header share button over the ADD TO ROUTE
  // button below: both live in different rows, so the offset is measured.
  // The share button is the header row's LAST element (pushed to the right
  // edge by the row's justify-between), so its position is set with a RIGHT
  // margin — the distance from the panel content's right edge to the center
  // of the ADD TO ROUTE button below. marginLeft would only add invisible
  // space inside the flex row and never move the button itself.
  const addRouteBtnRef = useRef<HTMLButtonElement | null>(null)
  const closeBtnRef = useRef<HTMLButtonElement | null>(null)
  const [shareOffset, setShareOffset] = useState<number | null>(null)
  useLayoutEffect(() => {
    const btn = addRouteBtnRef.current
    const closeBtn = closeBtnRef.current
    if (!btn || !closeBtn) return
    const measure = () => {
      // The share button sits just LEFT of the close (X) button in the header
      // row, separated by the row's gap-1 (4px) — it is no longer flush with
      // the row's right edge. Anchor to the X button's left edge minus that
      // gap: stable regardless of the panel padding, and it never moves when
      // the share button's own marginRight changes (no feedback loop).
      const closeRect = closeBtn.getBoundingClientRect()
      const btnRect = btn.getBoundingClientRect()
      const shareRight = closeRect.left - 4
      setShareOffset(shareRight - (btnRect.left + btnRect.width / 2) - 22)
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(btn)
    ro.observe(closeBtn)
    window.addEventListener('resize', measure)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [road?.id])
  const shareBtnStyle =
    shareOffset != null ? { marginRight: shareOffset } : undefined
  // When the selected road is a trimmed visit in the current route, the chips
  // describe the KEPT section (mileage scaled by the kept fraction) instead of
  // the full road. Selecting the road outside the route shows full values.
  const selectedTrim =
    road && selectedVisitKey ? routeTrims[selectedVisitKey] ?? null : null
  const keptMi =
    road && selectedTrim && typeof road.distance_miles === 'number'
      ? road.distance_miles * trimKeptFraction(selectedTrim)
      : null
  const distanceMiles =
    keptMi != null && road ? (keptMi as number) : (road?.distance_miles ?? null)
  const durationLabel = road
    ? formatDuration(trimScaledDurationSeconds(road, selectedTrim) ?? roadDurationSeconds(road))
    : null
  const roughEstimate = road ? isRoughEstimate(road) : false

  // Share: copy this road's permanent public URL (roadratings.com/road/[id])
  // and flash a small "URL copied" popup over the button.
  const handleShare = async () => {
    if (!road || shareCopied) return
    const url = await roadShareUrl(road.id)
    if (!url) {
      toast.error('Share link not ready yet — try again in a moment.')
      return
    }
    const ok = await copyText(url)
    if (ok) {
      setShareCopied(true)
      if (shareCopiedTimer.current) clearTimeout(shareCopiedTimer.current)
      shareCopiedTimer.current = setTimeout(() => setShareCopied(false), 1600)
    } else {
      toast.error('Could not copy the link — copy it from the address bar instead.')
    }
  }

  const forceResetRoad = async () => {
    if (!road || resetting) return
    setResetting(true)
    // Visual cue: the polyline drops off the map the moment the recalc
    // starts and reappears once the fresh geometry is back.
    setRecalculating(road.id)
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData.session?.access_token
      if (!token) {
        toast.error('Sign in first to reset this road.')
        setResetting(false)
        setRecalculating(null)
        return
      }
      const { data, error } = await supabase.functions.invoke('road-force-reset', {
        body: { roadId: road.id },
      })
      if (error) throw new Error(error.message || 'Reset failed')
      const rebuilt = data?.road as typeof road | undefined
      if (!rebuilt?.id) throw new Error('No road data returned')
      invalidateRoadGeometry(rebuilt.id)
      replaceRoad(rebuilt)
      // Popup is deferred: the map clears the recalculating flag itself (via
      // recalcDrawnId below) once the fresh polyline is resolved AND drawn —
      // the toast fires there, not here.
      setRecalcWatchId(rebuilt.id)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Reset failed. Try again.')
      setResetting(false)
      setRecalculating(null)
      setRecalcWatchId(null)
    }
  }

  // Interp Road Geometry: moderator-only diagnostic re-route. Densifies the
  // road's raw sheet waypoints with haversine midpoints and re-runs OSRM so
  // the route is FORCED through those mid-road points — purely visual/diagnostic
  // for this session, nothing is written back (no sheet write, no cache
  // overwrite). Explicitly ONE road at a time: guarded by selectedId + flag.
  const interpRoad = async () => {
    // Disabled 9/13/26: the interp trial ladder never found a matching route
    // on real roads (Southwick Rd, CO 125). Kept as a stub so the button
    // explains itself instead of failing.
    toast.info('9/13/26 - Disabled due to not being able to get it to work', {
      duration: 8000,
    })
    return
    if (!road || interpRunning || resetting) return
    setInterpRunning(true)
    setRecalculating(road.id)
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData.session?.access_token
      if (!token) {
        toast.error('Sign in first to run geometry interp.')
        setInterpRunning(false)
        setRecalculating(null)
        return
      }
      const { data, error } = await supabase.functions.invoke('road-force-reset', {
        body: { roadId: road.id, interp: true },
      })
      if (error) throw new Error(error.message || 'Interp failed')
      const rebuilt = data?.road as typeof road | undefined
      if (!rebuilt?.id) throw new Error('No road data returned')
      invalidateRoadGeometry(rebuilt.id)
      replaceRoad(rebuilt)

      // Trial ladder result: the backend tries the straight-line midpoints,
      // then ±30% perpendicular offsets, and reports whether ANY trial's OSRM
      // route came within 10% of the sheet's expected mileage. If none did,
      // the closest attempt is drawn on the map but no URL is produced — the
      // popup says so explicitly.
      const interpOk = data?.interp_ok === true
      const routeMiles = typeof data?.interp_route_miles === 'number' ? data.interp_route_miles : null
      const expectedMiles = typeof data?.interp_expected_miles === 'number' ? data.interp_expected_miles : null
      if (!interpOk) {
        toast.error(
          `"${rebuilt.name}" — Interp could not find a route matching the expected one.`,
          {
            description:
              routeMiles !== null && expectedMiles !== null
                ? `Best attempt: ${routeMiles.toFixed(1)} mi vs expected ${expectedMiles.toFixed(1)} mi (tolerance ±10%). The closest route is drawn on the map for inspection.`
                : 'The closest route is drawn on the map for inspection.',
            duration: 12000,
          },
        )
        return
      }

      // Build a Google Maps driving URL representing the interpolated route
      // and copy it to the clipboard for the moderator. navigator.clipboard
      // is blocked inside sandboxed iframes, so fall back to a
      // hidden-textarea execCommand copy; the popup always shows the URL with
      // a manual Copy/Open option as a last resort.
      //
      // The URL must represent the NEW interp path, not restate the original
      // link. Google Maps URL routing caps a route at ~10 waypoints (start +
      // 8 vias + end) and silently drops the rest, so the URL keeps the
      // ORIGINAL waypoints' start and end fixed and threads it through up to
      // 3 NEW via points selected from the interp chain (original waypoints +
      // haversine midpoints). Selection = greatest angular deviation from the
      // straight corridor between the original waypoints, i.e. the midpoints
      // that pull the route furthest off the original straight-line path —
      // exactly where OSRM snapped to a different road.
      const waypoints = (data?.interp_waypoints ?? null) as [number, number][] | null
      const originalWaypoints = (data?.original_waypoints ?? null) as [number, number][] | null
      const newGeometry = rebuilt.geometry?.type === 'LineString'
        ? (rebuilt.geometry.coordinates as [number, number][])
        : null
      const toRad = (d: number) => (d * Math.PI) / 180
      // Perpendicular offset of p from segment a→b (approx equirectangular).
      const crossTrack = (p: [number, number], a: [number, number], b: [number, number]) => {
        const latScale = Math.cos(toRad((a[1] + b[1]) / 2))
        const ax = a[0] * latScale, ay = a[1]
        const bx = b[0] * latScale, by = b[1]
        const px = p[0] * latScale, py = p[1]
        const dx = bx - ax, dy = by - ay
        const len = Math.hypot(dx, dy)
        if (len < 1e-9) return Math.hypot(px - ax, py - ay)
        return Math.abs(((px - ax) * dy - (py - ay) * dx) / len)
      }
      // Corridor = straight lines between consecutive ORIGINAL waypoints. The
      // interp midpoints lie ON this corridor by construction, so candidates
      // must come from the NEW OSRM polyline — its vertices deviate exactly
      // where the interpolated route diverges from the original snap.
      const legs: [number, number][][] = []
      if (originalWaypoints && originalWaypoints.length >= 2) {
        for (let i = 0; i < originalWaypoints.length - 1; i++) {
          legs.push([originalWaypoints[i], originalWaypoints[i + 1]])
        }
      } else if (waypoints && waypoints.length >= 2) {
        legs.push([waypoints[0], waypoints[waypoints.length - 1]])
      }
      const maxDevFromOriginal = (p: [number, number]) =>
        legs.reduce((max, [a, b]) => Math.max(max, crossTrack(p, a, b)), 0)

      const MAX_NEW_VIAS = 3
      const MIN_VIA_DEVIATION = 0.0008 // ~90 m off the original corridor
      let vias: [number, number][] = []

      if (newGeometry && newGeometry.length > 2 && legs.length > 0) {
        // Even-spread apportionment. Locate each original waypoint on the new
        // polyline (nearest vertex, monotonic in travel order), then split
        // every leg into equal-length slots so picked vias are distributed
        // EVENLY along the whole route instead of clustering on adjacent
        // hairpins. Within each slot we keep the vertex that deviates most
        // from the original straight-line corridor — that is where the
        // interpolated route diverges from the original snap.
        const anchors: number[] = []
        const anchorWps: [number, number][] =
          originalWaypoints && originalWaypoints.length >= 2
            ? originalWaypoints
            : [newGeometry[0], newGeometry[newGeometry.length - 1]]
        let cursor = 0
        for (const [lng, lat] of anchorWps) {
          let best = cursor
          let bestD = Infinity
          for (let i = cursor; i < newGeometry.length; i++) {
            const d = (newGeometry[i][0] - lng) ** 2 + (newGeometry[i][1] - lat) ** 2
            if (d < bestD) {
              bestD = d
              best = i
            }
          }
          anchors.push(best)
          cursor = best
        }
        const legLens = legs.map((_, i) => {
          let s = 0
          for (let j = anchors[i]; j < anchors[i + 1]; j++) {
            s += Math.hypot(
              newGeometry[j + 1][0] - newGeometry[j][0],
              newGeometry[j + 1][1] - newGeometry[j][1],
            )
          }
          return s
        })
        // Apportion MAX_NEW_VIAS across legs by length (largest length/slot wins).
        const slots = legs.map(() => 0)
        for (let n = 0; n < MAX_NEW_VIAS; n++) {
          let best = 0
          let bestScore = -1
          legs.forEach((_, i) => {
            const score = legLens[i] / (slots[i] + 1)
            if (score > bestScore) {
              bestScore = score
              best = i
            }
          })
          slots[best]++
        }
        const picked: { p: [number, number]; idx: number }[] = []
        legs.forEach((_, i) => {
          const a = anchors[i]
          const b = anchors[i + 1]
          const n = slots[i]
          if (n === 0 || b - a < 4) return
          const span = b - a
          for (let s = 0; s < n; s++) {
            const from = a + Math.round(((s + 1) * span) / (n + 1))
            const to = a + Math.round(((s + 2) * span) / (n + 1))
            let bestP: [number, number] | null = null
            let bestIdx = -1
            let bestDev = MIN_VIA_DEVIATION
            for (let j = Math.max(from, a + 1); j < Math.min(to, b); j++) {
              const dev = maxDevFromOriginal(newGeometry[j])
              if (dev > bestDev) {
                bestDev = dev
                bestP = newGeometry[j]
                bestIdx = j
              }
            }
            if (bestP) picked.push({ p: bestP, idx: bestIdx })
          }
        })
        picked.sort((a, b) => a.idx - b.idx)
        vias = picked.map((q) => q.p)
      } else if (waypoints && waypoints.length > 2) {
        // No fresh OSRM polyline — fall back to evenly spaced interp points
        // between the fixed endpoints (Google Maps caps at ~10 waypoints).
        const MAX_URL_WAYPOINTS = 10
        if (waypoints.length <= MAX_URL_WAYPOINTS) {
          vias = waypoints.slice(1, -1)
        } else {
          for (let i = 1; i < MAX_URL_WAYPOINTS - 1; i++) {
            vias.push(
              waypoints[Math.round((i * (waypoints.length - 1)) / (MAX_URL_WAYPOINTS - 1))],
            )
          }
        }
      }

      let interpUrl: string | null = null
      if (waypoints && waypoints.length >= 2) {
        const chain: [number, number][] = [
          waypoints[0],
          ...vias,
          waypoints[waypoints.length - 1],
        ]
        const leg = (n: number) => n.toFixed(6)
        const path = chain.map(([lng, lat]) => `${leg(lat)},${leg(lng)}`).join('/')
        interpUrl = `https://www.google.com/maps/dir/${path}/data=!4m2!4m1!3e0`
      }
      const originalUrl = road.google_maps_url
      const urlIsUnchanged = interpUrl !== null && originalUrl !== null && interpUrl === originalUrl
      let copied = false
      if (interpUrl && !urlIsUnchanged) copied = await copyToClipboard(interpUrl)
      toast.success(
        urlIsUnchanged
          ? `"${rebuilt.name}" interpolated — route shape unchanged, the Google Maps URL matches the original.`
          : `"${rebuilt.name}" interpolated successfully.`,
        {
        description: interpUrl ? (
          <span className="flex flex-col gap-1">
            <span>
              {copied
                ? 'Google Maps URL copied to clipboard.'
                : 'Clipboard blocked — copy the URL below manually.'}
            </span>
            <a
              href={interpUrl}
              target="_blank"
              rel="noreferrer"
              className="underline underline-offset-2 break-all text-emerald-300 hover:text-emerald-200"
              onClick={(e) => e.stopPropagation()}
            >
              Open in Google Maps ↗
            </a>
          </span>
        ) : undefined,
          duration: 12000,
          action: interpUrl && !copied
            ? {
                label: 'Copy URL',
                onClick: () => {
                  void copyToClipboard(interpUrl!)
                  toast.success('URL copied to clipboard.')
                },
              }
            : undefined,
        },
      )
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Interp failed. Try again.')
    } finally {
      setInterpRunning(false)
      setRecalculating(null)
      setRecalcWatchId(null)
    }
  }

  // Success popup: fires the moment the map confirms the recalculated road's
  // fresh polyline has been resolved AND drawn back onto the map — never
  // before the road is actually visible again.
  useEffect(() => {
    if (recalcDrawnId === null) return
    const drawn = roads.find((r) => r.id === recalcDrawnId)
    if (drawn) {
      toast.success(`"${drawn.name}" re-synced from the sheet and geometry recalculated from OSRM.`)
      setResetting(false)
    }
    setRecalcDrawn(null)
    setRecalcWatchId(null)
    setRecalculating(null)
  }, [recalcDrawnId, roads, setRecalcDrawn, setRecalcWatchId, setRecalculating])

  // Watchdog: if the map never confirms the redraw (geometry resolve failed,
  // OSRM unreachable, panel closed mid-recalc), release the spinner after a
  // grace period instead of hanging on "RECALCULATING…" forever.
  useEffect(() => {
    if (recalcWatchId === null) return
    const t = window.setTimeout(() => {
      setRecalcDrawn(null)
      setRecalcWatchId(null)
      setRecalculating(null)
      setResetting(false)
      toast.error('Recalc done, but the map could not redraw this road in time — it will appear after a refresh.')
    }, 30000)
    return () => window.clearTimeout(t)
  }, [recalcWatchId, setRecalcDrawn, setRecalcWatchId, setRecalculating])

  const submitFlag = async () => {
    if (!road || !reason.trim()) return
    setSubmitting(true)
    const { error } = await supabase.from('road_flags').insert({
        user_id: session?.user.id ?? null,
      road_id: road.id,
      road_name: road.name,
      reason: reason.trim(),
      details: {},
    })
    setSubmitting(false)
    if (error) {
      toast.error('Could not submit flag. Try again.')
    } else {
      toast.success('Thanks — the founder will review this road.')
      setFlagOpen(false)
      setReason('')
    }
  }

  const ratings = road?.ratings ?? null
  // All four sub-ratings always shown — a missing value renders as "-".
  const ratingRows: [string, string, number | null][] = ratings
    ? (
        [
          ['fun', 'Fun'],
          ['pavement', 'Pavement Quality'],
          ['traffic', 'Dirt / Debris'],
          ['dirt', 'Traffic'],
        ] as const
      ).map(([k, label]) => [k, label, (ratings as Record<string, number | null>)[k] ?? null])
    : []
  // Elevation profile (Bay Area roads, cached with the geometry).
  useElevationVersion()
  const elevation = road ? roadElevation(road) : null

  return (
    <AnimatePresence>
      {road && (
        <motion.aside
          key={road.id}
          // Guarded callback ref: when a second road is selected, the OLD
          // panel stays mounted for its exit animation, then unmounts — and
          // React detaches its ref with null, clobbering the NEW panel's
          // ref. Ignoring null keeps asideRef pointing at the live panel so
          // direct-DOM drag writes keep working after a road switch.
          ref={(el) => {
            if (el) asideRef.current = el
          }}
          initial={{ opacity: 0, y: 80 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 80 }}
          transition={{ type: 'spring', damping: 28, stiffness: 300 }}
          data-road-panel="true"
          style={sheetH != null ? { height: sheetH, maxHeight: sheetH } : undefined}
          className="fixed md:absolute z-50 bg-[#0C0C0E]/95 md:backdrop-blur border-2 border-white
            inset-x-0 bottom-0 max-h-[40vh] overflow-hidden rounded-t-2xl flex flex-col
            md:inset-auto md:right-3 md:bottom-3 md:top-auto md:w-96 md:max-h-[calc(100dvh-100px)] md:rounded-xl"
        >
          <div
            onPointerDown={onDragStart}
            onPointerMove={onDragMove}
            onPointerUp={onDragEnd}
            onPointerCancel={onDragEnd}
            className="shrink-0 z-10 bg-[#0C0C0E] select-none touch-none md:cursor-default md:active:cursor-default"
          >
            <div className="flex justify-center pt-2.5 pb-2 md:hidden">
              <div className="w-10 h-1 rounded-full bg-white/25" />
            </div>
            <div className="px-5 pb-4 space-y-4 md:pt-4">
              <div className="relative flex items-start justify-between gap-3">
              <div className="min-w-0 max-w-[50%] md:max-w-[50%] flex-1">
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">
                  {road.region} · {road.group}
                </div>
                <h2 className="text-xl font-bold tracking-tight text-white mt-1 leading-tight break-words">
                  {road.name}
                </h2>
              </div>
                <div className="flex items-start gap-1 shrink-0 self-stretch">
                  <div className="relative self-end -mb-2.5">
                    <button
                      onClick={handleShare}
                      style={shareBtnStyle}
                      className="h-11 w-11 flex items-center justify-center rounded-lg text-white/50 hover:text-white"
                      title="Copy share link"
                    >
                      <Share2 size={17} />
                    </button>
                    <AnimatePresence>
                      {shareCopied && (
                        <motion.div
                          initial={{ opacity: 0, y: 6, scale: 0.9 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 6, scale: 0.9 }}
                          transition={{ duration: 0.15 }}
                          className="absolute top-full right-0 mt-2 whitespace-nowrap rounded-md bg-[#0C0C0E] border border-white px-2.5 py-1 text-[11px] font-medium text-white/90 shadow-lg pointer-events-none z-20"
                        >
                          URL copied
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                  <button
                    ref={closeBtnRef}
                    onClick={() => selectRoad(null)}
                    aria-label="Close road details"
                    title="Close"
                    className="h-11 w-11 -mt-4 -mr-1.5 flex items-center justify-center rounded-lg text-white/40 hover:text-white/70 transition-colors"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div ref={scrollBodyRef} className="overflow-y-auto min-h-0 flex-1 overscroll-contain px-5 pb-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className="text-5xl font-bold tracking-tight text-amber-400 leading-none">
                {(ratings?.overall ?? 0).toFixed(1)}
              </div>
              <div className="text-sm text-white/40">/ 10 overall</div>
              {/* Roads may be added multiple times — each visit is trimmed
                  separately — so this never disables. */}
              <button
                ref={addRouteBtnRef}
                onClick={() => addToRoute(road.id)}
                title={
                  routeIds.includes(road.id)
                    ? 'Add another visit to this road'
                    : 'Add this road to your route'
                }
                className="ml-auto min-h-11 px-4 rounded-lg bg-amber-400 hover:bg-amber-300 text-black font-bold text-sm tracking-wide flex items-center justify-center gap-2"
              >
                <Plus size={15} /> {routeIds.includes(road.id) ? 'ADD AGAIN' : 'ADD TO ROUTE'}
              </button>
              {routeIds.includes(road.id) && (
                <button
                  onClick={() => {
                    // With duplicate visits allowed, TRIM targets the LAST
                    // visit of this road in the route (the pass the rider is
                    // most likely working on), keyed per occurrence.
                    let lastIdx = -1
                    for (let i = 0; i < routeIds.length; i++) if (routeIds[i] === road.id) lastIdx = i
                    setTrimmingRoad(occurrenceKey(road.id, occurrenceOf(routeIds, lastIdx)))
                  }}
                  title="Trim excluded sections of this road"
                  className="min-h-11 px-3 rounded-lg border border-amber-400/40 text-amber-400 hover:bg-amber-400/10 font-bold text-sm flex items-center justify-center"
                >
                  TRIM
                </button>
              )}
            </div>

            {ratingRows.length > 0 && (
              <div className="grid grid-cols-4 gap-2 pt-1">
                {ratingRows.map(([k, label, value]) => (
                  <div key={k} className="flex flex-col min-w-0">
                    <span className="text-[14px] leading-tight text-white/40 break-words min-h-[36px]">{label}</span>
                    <span className={`text-lg font-mono ${value != null && value >= 9 ? 'text-amber-400' : 'text-white/80'}`}>
                      {value != null ? value.toFixed(1) : '-'}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-wrap gap-1.5">
              {distanceMiles != null && (
                <span className="px-2.5 py-1.5 rounded-lg bg-white/[0.05] border border-white/10 text-xs text-white/70 flex items-center gap-1.5">
                  <Route size={13} className="text-amber-400" />
                  <span className="whitespace-nowrap">
                    {distanceMiles > 5
                      ? `${Math.round(distanceMiles)} mi`
                      : (() => {
                          const s = String(distanceMiles)
                          const dot = s.indexOf('.')
                          if (dot === -1) return `${s} mi`
                          return (
                            <>
                              {s.slice(0, dot)}
                              <span className="text-[0.75em] align-baseline">{s.slice(dot)}</span> mi
                            </>
                          )
                        })()}
                  </span>
                </span>
              )}
              {durationLabel && (
                <span
                  onClick={roughEstimate ? () => setShowTimeTip((v) => !v) : undefined}
                  className={`relative group px-2.5 py-1.5 rounded-lg text-xs flex items-center gap-1.5 border border-white/10 text-white/70 ${
                    roughEstimate ? 'cursor-help' : 'bg-white/[0.05]'
                  }`}
                  style={
                    roughEstimate
                      ? {
                          backgroundImage:
                            'repeating-linear-gradient(45deg, rgba(255,255,255,0.28) 0px, rgba(255,255,255,0.28) 4px, rgba(255,255,255,0.06) 4px, rgba(255,255,255,0.06) 8px)',
                        }
                      : undefined
                  }
                >
                  <Timer size={13} className="text-amber-400" /> {durationLabel}
                  {roughEstimate && '~'}
                  {roughEstimate && (
                    <span
                      className={`pointer-events-none absolute bottom-full left-0 mb-1.5 w-56 z-20 rounded-lg border border-white/15 bg-zinc-900/95 px-2.5 py-2 text-[11px] leading-snug text-white/80 shadow-lg ${
                        showTimeTip ? 'block' : 'hidden'
                      } group-hover:block`}
                    >
                      This is a very rough ride time estimate due to technical limitations.
                    </span>
                  )}
                </span>
              )}
              <span className="px-2.5 py-1.5 rounded-lg bg-white/[0.05] border border-white/10 text-xs text-white/70 flex items-center gap-1.5">
                {road.fully_paved === true ? (
                  <CheckCircle2 size={13} className="text-emerald-400" />
                ) : road.fully_paved === false ? (
                  <XCircle size={13} className="text-white/40" />
                ) : (
                  <HelpCircle size={13} className="text-white/40" />
                )}
                Fully paved: {road.fully_paved === true ? 'Yes' : road.fully_paved === false ? 'No' : '?'}
              </span>
              <div className="flex flex-wrap gap-1.5">
                {road.bike_types.map((b) => (
                  <BikeTypeTip key={b} type={b}>
                    <span
                      className="px-2.5 py-1.5 rounded-lg bg-white/[0.05] border border-white/10 text-xs text-white/70 flex items-center gap-1.5"
                    >
                      <span className="flex flex-col items-stretch gap-0.5">
                        {b}
                        <span
                          className="h-[2px] rounded-full"
                          style={{ background: BIKE_COLORS[b] ?? '#888' }}
                        />
                      </span>
                    </span>
                  </BikeTypeTip>
                ))}
              </div>
              <span className="px-2.5 py-1.5 rounded-lg bg-white/[0.05] border border-white/10 text-xs text-white/50">
                {road.region}
              </span>
              <span className="px-2.5 py-1.5 rounded-lg bg-white/[0.05] border border-white/10 text-xs text-white/50">
                {road.group}
              </span>
            </div>

            {<ElevationChart elevation={elevation ?? []} />}

            <p className="text-sm text-white/70 leading-relaxed">
              {road.description}
            </p>

            {road.google_maps_url && (
              <a
                href={road.google_maps_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-white/50 hover:text-white/80"
              >
                <ExternalLink size={13} /> Open in Google Maps
              </a>
            )}

            {flagOpen && (
              <div className="rounded-lg border border-white bg-white/[0.03] p-3 space-y-2">
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/70">
                  What's wrong with this road's info?
                </div>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  placeholder="e.g. Road is closed on weekdays, rating feels too high…"
                  className="w-full rounded-lg bg-black/40 border border-white/10 p-2.5 text-sm text-white placeholder:text-white/30 outline-none focus:border-amber-400/60 resize-none"
                />
                <button
                  onClick={submitFlag}
                  disabled={!reason.trim() || submitting}
                  className="min-h-11 w-full rounded-lg border border-amber-400/50 text-amber-300 text-sm font-medium disabled:opacity-40"
                >
                  {submitting ? 'Submitting…' : 'Submit flag'}
                </button>
              </div>
            )}

            <div className="space-y-2 pt-1">
              <button
                ref={flagBtnRef}
                onClick={toggleFlag}
                className="min-h-12 w-full rounded-lg border border-white hover:border-white text-white/50 hover:text-white/80 text-sm flex items-center justify-center gap-2"
              >
                <Flag size={14} /> FLAG ROAD INFO
              </button>
              {isModerator && (
                <button
                  onClick={forceResetRoad}
                  disabled={resetting}
                  className="min-h-11 w-full rounded-lg text-white/40 hover:text-white/70 text-xs font-mono tracking-wide flex items-center justify-center gap-2 disabled:opacity-40"
                >
                  <RefreshCw size={13} className={resetting ? 'animate-spin' : ''} />
                  {resetting ? 'RECALCULATING…' : 'FORCE ROAD RECALC'}
                </button>
              )}
              {isModerator && (
                <button
                  onClick={interpRoad}
                  disabled={interpRunning || resetting}
                  className="min-h-11 w-full rounded-lg text-white/40 hover:text-white/70 text-xs font-mono tracking-wide flex items-center justify-center gap-2 disabled:opacity-40"
                >
                  <Spline size={13} className={interpRunning ? 'animate-pulse' : ''} />
                  {interpRunning ? 'INTERPOLATING…' : 'INTERP ROAD GEOMETRY'}
                </button>
              )}
              {isModerator && (
                <button
                  onClick={() => setHideOpen(true)}
                  className="min-h-11 w-full rounded-lg text-white/40 hover:text-white/70 text-xs font-mono tracking-wide flex items-center justify-center gap-2"
                >
                  <EyeOff size={13} />
                  HIDE ROAD
                </button>
              )}
            </div>

            {/* Hide Road (moderator): reason + review date, confirm-gated */}
            {isModerator && hideOpen && road && (
              <div
                className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 p-4"
                onClick={() => setHideOpen(false)}
              >
                <div
                  className="w-full max-w-sm rounded-xl bg-zinc-900 border border-white/10 shadow-2xl p-5 space-y-4"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-white">Hide this road</h3>
                    <button
                      onClick={() => setHideOpen(false)}
                      aria-label="Cancel"
                      className="text-white/40 hover:text-white/80 p-1"
                    >
                      <X size={16} />
                    </button>
                  </div>
                  <p className="text-xs text-white/50">
                    "{road.name}" will disappear from the map and search for everyone until it is
                    restored.
                  </p>
                  <div className="space-y-1.5">
                    <label htmlFor="hide-reason" className="text-xs text-white/60">
                      Why hide this road?
                    </label>
                    <textarea
                      id="hide-reason"
                      value={hideReason}
                      onChange={(e) => setHideReason(e.target.value)}
                      rows={3}
                      placeholder="e.g. Road closed — OSRM cannot route it"
                      className="w-full rounded-lg bg-black/40 border border-white/10 focus:border-amber-400/60 outline-none text-sm text-white placeholder:text-white/25 px-3 py-2 resize-none"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label htmlFor="hide-review-date" className="text-xs text-white/60">
                      When to review the hide?
                    </label>
                    <div className="relative">
                      <input
                        id="hide-review-date"
                        type="date"
                        value={hideReviewDate}
                        onChange={(e) => setHideReviewDate(e.target.value)}
                        className="w-full rounded-lg bg-black/40 border border-white/10 focus:border-amber-400/60 outline-none text-sm text-white px-3 py-2 pr-10 [color-scheme:dark] [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
                      />
                      <button
                        type="button"
                        tabIndex={-1}
                        aria-label="Open date picker"
                        onClick={(e) => {
                          const input = e.currentTarget.previousElementSibling as HTMLInputElement
                          try {
                            input.showPicker()
                          } catch {
                            input.focus()
                            input.click()
                          }
                        }}
                        className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-white/50 hover:text-amber-400 cursor-pointer"
                      >
                        <Calendar className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <button
                    onClick={confirmHideRoad}
                    disabled={!hideReason.trim() || !hideReviewDate || hideSubmitting}
                    className="min-h-11 w-full rounded-lg border border-amber-400/60 text-amber-300 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {hideSubmitting ? 'Hiding…' : 'Confirm hide'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  )
}
